import api from './api';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// // Create axios instance with default config
// const api = axios.create({
//   baseURL: API_URL,
//   withCredentials: true,
//   headers: {
//     'Content-Type': 'application/json',
//   },
// });

const ccmService = {
  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  /**
   * Get CCM configuration for a practice
   */
  getConfiguration: async (practiceId) => {
    const response = await api.get(`/ccm/configuration/${practiceId}`);
    return response.data;
  },

  /**
   * Save CCM configuration
   */
  saveConfiguration: async (practiceId, configData) => {
    const response = await api.post(`/ccm/configuration/${practiceId}`, configData);
    return response.data;
  },

  // ============================================================================
  // ENROLLMENT
  // ============================================================================

  /**
   * Get enrollment queue
   */
  getEnrollmentQueue: async (filters = {}) => {
    const response = await api.get('/ccm/enrollment/queue', { params: filters });
    return response.data;
  },

  /**
   * Check patient eligibility
   */
  checkEligibility: async (patientId, programType = 'CCM') => {
    const response = await api.get(`/ccm/enrollment/check-eligibility/${patientId}`, {
      params: { programType }
    });
    return response.data;
  },

  /**
   * Enroll patient
   */
  enrollPatient: async (enrollmentData) => {
    const response = await api.post('/ccm/enrollment/enroll', enrollmentData);
    return response.data;
  },

  /**
   * Get enrolled patients
   */
  getEnrolledPatients: async (filters = {}) => {
    const response = await api.get('/ccm/enrollment/enrolled', { params: filters });
    return response.data;
  },

  /**
   * Disenroll patient
   */
  disenrollPatient: async (enrollmentId, disenrollmentData) => {
    const response = await api.post(`/ccm/enrollment/${enrollmentId}/disenroll`, disenrollmentData);
    return response.data;
  },

  getEnrollmentByPatient: async (patientId, programType) => {
    const response = await api.get(`/ccm/enrollment/patient/${patientId}`, {
      params: programType ? { programType } : {},
    });
    return response.data;
  },

  updateEnrollmentComplexity: async (enrollmentId, payload) => {
    const response = await api.patch(`/ccm/enrollment/${enrollmentId}/complexity`, payload);
    return response.data;
  },

  // ============================================================================
  // TIME TRACKING
  // ============================================================================

  /**
   * Get time logs
   */
  getTimeLogs: async (filters = {}) => {
    const response = await api.get('/ccm/time-tracking/logs', { params: filters });
    return response.data;
  },

  /**
   * Add time entry manually
   */
  addTimeManually: async (timeData) => {
    const response = await api.post('/ccm/time-tracking/add', timeData);
    return response.data;
  },

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  // ============================================================================
  // CONFIGURATION - ICD GROUPS & INSURANCE
  // ============================================================================

  /**
   * Get ICD groups
   */
  getIcdGroups: async (practiceId) => {
    const response = await api.get(`/ccm/configuration/${practiceId}/icd-groups`);
    return response.data;
  },

  /**
   * Create ICD group
   */
  createIcdGroup: async (practiceId, groupData) => {
    const response = await api.post(`/ccm/configuration/${practiceId}/icd-groups`, groupData);
    return response.data;
  },

  /**
   * Get eligible insurances
   */
  getEligibleInsurances: async (practiceId) => {
    const response = await api.get(`/ccm/configuration/${practiceId}/insurances`);
    return response.data;
  },

  /**
   * Add eligible insurance
   */
  addEligibleInsurance: async (practiceId, insuranceData) => {
    const response = await api.post(`/ccm/configuration/${practiceId}/insurances`, insuranceData);
    return response.data;
  },

  /**
   * Get time capture reasons
   */
  getTimeCaptureReasons: async (practiceId = null) => {
    const response = await api.get('/ccm/configuration/time-capture-reasons', {
      params: { practiceId }
    });
    return response.data;
  },

  /**
   * Get disenrollment reasons
   */
  getDisenrollmentReasons: async (practiceId = null) => {
    const response = await api.get('/ccm/configuration/disenrollment-reasons', {
      params: { practiceId }
    });
    return response.data;
  },

  // ============================================================================
  // CARE PLANS
  // ============================================================================

  /**
   * Get care plan templates
   */
  getCarePlanTemplates: async (practiceId, templateType = 'CCM') => {
    const response = await api.get('/ccm/care-plans/templates', {
      params: { practiceId, templateType }
    });
    return response.data;
  },

  /**
   * Get care plans for enrollment
   */
  getCarePlans: async (enrollmentId) => {
    const response = await api.get(`/ccm/care-plans/${enrollmentId}`);
    return response.data;
  },

  /**
   * Create care plan
   */
  createCarePlan: async (carePlanData) => {
    const response = await api.post('/ccm/care-plans/create', carePlanData);
    return response.data;
  },

  saveDiseaseCarePlan: async (enrollmentId, data) => {
    const response = await api.post(`/ccm/care-plans/${enrollmentId}/disease-plan`, data);
    return response.data;
  },

  deleteDiseaseCarePlan: async (carePlanId) => {
    const response = await api.delete(`/ccm/care-plans/${carePlanId}/disease-plan`);
    return response.data;
  },

  /**
   * Approve care plan
   */
  approveCarePlan: async (carePlanId) => {
    const response = await api.post(`/ccm/care-plans/${carePlanId}/approve`);
    return response.data;
  },

  rejectCarePlan: async (carePlanId) => {
    const response = await api.post(`/ccm/care-plans/${carePlanId}/reject`);
    return response.data;
  },

  // ============================================================================
  // CARE PLAN WORKSPACE
  // ============================================================================

  getCarePlanWorkspace: async (enrollmentId) => {
    const response = await api.get(`/ccm/care-plans/${enrollmentId}/workspace`);
    return response.data;
  },

  createFullCarePlan: async (enrollmentId, data) => {
    const response = await api.post(`/ccm/care-plans/${enrollmentId}/create-full`, data);
    return response.data;
  },

  activateCarePlan: async (carePlanId) => {
    const response = await api.post(`/ccm/care-plans/${carePlanId}/activate`);
    return response.data;
  },

  getCarePlanDetail: async (carePlanId) => {
    const response = await api.get(`/ccm/care-plans/plan/${carePlanId}/detail`);
    return response.data;
  },

  addCarePlanCondition: async (enrollmentId, data) => {
    const response = await api.post(`/ccm/care-plans/${enrollmentId}/conditions`, data);
    return response.data;
  },

  updateCarePlanCondition: async (conditionId, data) => {
    const response = await api.put(`/ccm/care-plans/conditions/${conditionId}`, data);
    return response.data;
  },

  deleteCarePlanCondition: async (conditionId) => {
    const response = await api.delete(`/ccm/care-plans/conditions/${conditionId}`);
    return response.data;
  },

  addCarePlanProblem: async (enrollmentId, data) => {
    const response = await api.post(`/ccm/care-plans/${enrollmentId}/problems`, data);
    return response.data;
  },

  updateCarePlanProblem: async (problemId, data) => {
    const response = await api.put(`/ccm/care-plans/problems/${problemId}`, data);
    return response.data;
  },

  deleteCarePlanProblem: async (problemId) => {
    const response = await api.delete(`/ccm/care-plans/problems/${problemId}`);
    return response.data;
  },

  addCarePlanGoal: async (enrollmentId, data) => {
    const response = await api.post(`/ccm/care-plans/${enrollmentId}/goals`, data);
    return response.data;
  },

  updateCarePlanGoal: async (goalId, data) => {
    const response = await api.put(`/ccm/care-plans/goals/${goalId}`, data);
    return response.data;
  },

  deleteCarePlanGoal: async (goalId) => {
    const response = await api.delete(`/ccm/care-plans/goals/${goalId}`);
    return response.data;
  },

  addCarePlanObjective: async (goalId, data) => {
    const response = await api.post(`/ccm/care-plans/goals/${goalId}/objectives`, data);
    return response.data;
  },

  updateCarePlanObjective: async (objectiveId, data) => {
    const response = await api.put(`/ccm/care-plans/objectives/${objectiveId}`, data);
    return response.data;
  },

  deleteCarePlanObjective: async (objectiveId) => {
    const response = await api.delete(`/ccm/care-plans/objectives/${objectiveId}`);
    return response.data;
  },

  addCarePlanMedication: async (enrollmentId, data) => {
    const response = await api.post(`/ccm/care-plans/${enrollmentId}/medications`, data);
    return response.data;
  },

  updateCarePlanMedication: async (medicationId, data) => {
    const response = await api.put(`/ccm/care-plans/medications/${medicationId}`, data);
    return response.data;
  },

  deleteCarePlanMedication: async (medicationId) => {
    const response = await api.delete(`/ccm/care-plans/medications/${medicationId}`);
    return response.data;
  },

  getCarePlanTemplateDetail: async (templateId) => {
    const response = await api.get(`/ccm/care-plans/templates/${templateId}/detail`);
    return response.data;
  },

  applyCarePlanTemplate: async (enrollmentId, data) => {
    const response = await api.post(`/ccm/care-plans/${enrollmentId}/apply-template`, data);
    return response.data;
  },

  seedDefaultTemplateContent: async () => {
    const response = await api.post('/ccm/care-plans/templates/seed-defaults');
    return response.data;
  },

  // ============================================================================
  // ASSESSMENTS & VIRTUAL VISITS
  // ============================================================================

  approveCarePlanReviewItem: async (entityType, entityId) => {
    const response = await api.post(`/ccm/care-plans/review/${entityType}/${entityId}/approve`);
    return response.data;
  },

  rejectCarePlanReviewItem: async (entityType, entityId) => {
    const response = await api.post(`/ccm/care-plans/review/${entityType}/${entityId}/reject`);
    return response.data;
  },

  setCarePlanItemFollowing: async (entityType, entityId, isFollowing) => {
    const response = await api.patch(`/ccm/care-plans/review/${entityType}/${entityId}/following`, {
      is_following: isFollowing,
    });
    return response.data;
  },

  getAssessmentTemplates: async () => {
    const response = await api.get('/ccm/assessments/templates');
    return response.data;
  },

  getPatientAssessments: async (enrollmentId) => {
    const response = await api.get(`/ccm/assessments/patient/${enrollmentId}`);
    return response.data;
  },

  submitAssessment: async (assessmentData) => {
    const response = await api.post('/ccm/assessments/submit', assessmentData);
    return response.data;
  },

  startVirtualVisit: async (enrollmentId) => {
    const response = await api.post('/ccm/care-plans/virtual-visit/start', { enrollment_id: enrollmentId });
    return response.data;
  },

  // ============================================================================
  // TIMER
  // ============================================================================

  /**
   * Start timer
   */
  startTimer: async (timerData) => {
    const response = await api.post('/ccm/time-tracking/timer/start', timerData);
    return response.data;
  },

  /**
   * Stop timer
   */
  stopTimer: async (sessionId, description = '') => {
    const response = await api.post(`/ccm/time-tracking/timer/${sessionId}/stop`, { description });
    return response.data;
  },

  /**
   * Get active timers
   */
  getActiveTimers: async (userId) => {
    const response = await api.get('/ccm/time-tracking/timer/active', {
      params: { userId }
    });
    return response.data;
  },

  // ============================================================================
  // BILLING
  // ============================================================================

  /**
   * Get billing cycles
   */
  getBillingCycles: async (filters = {}) => {
    const response = await api.get('/ccm/billing/cycles', { params: filters });
    return response.data;
  },

  /**
   * Create billing cycle
   */
  createBillingCycle: async (cycleData) => {
    const response = await api.post('/ccm/billing/cycles', cycleData);
    return response.data;
  },

  /**
   * Get qualifying patients
   */
  getQualifyingPatients: async (practiceId, billingMonth) => {
    const response = await api.get('/ccm/billing/qualifying-patients', {
      params: { practiceId, billingMonth }
    });
    return response.data;
  },

  /**
   * Generate encounters
   */
  generateEncounters: async (encounterData) => {
    const response = await api.post('/ccm/billing/generate-encounters', encounterData);
    return response.data;
  },

  /**
   * Create claims
   */
  createClaims: async (claimsData) => {
    const response = await api.post('/ccm/billing/create-claims', claimsData);
    return response.data;
  },

  /**
   * Get claims
   */
  getClaims: async (filters = {}) => {
    const response = await api.get('/ccm/billing/claims', { params: filters });
    return response.data;
  },

  // ============================================================================
  // DASHBOARD
  // ============================================================================

  /**
   * Get dashboard statistics
   */
  getDashboardStats: async (practiceId, dateFrom = null, dateTo = null) => {
    const response = await api.get(`/ccm/dashboard/stats/${practiceId}`, {
      params: { dateFrom, dateTo }
    });
    return response.data;
  }
};

export default ccmService;

